/**
 * One place for the chat surface's shared visual decisions, so the full page and the drawer are the
 * same product rather than two things that drifted. Everything resolves through theme tokens, so it
 * follows the app's light and dark palettes instead of hardcoding a second one.
 */

/** Reading measure. Long answers with tables need a column, not the full width of a monitor. */
export const COLUMN = {
  width: '100%',
  maxWidth: 760,
  mx: 'auto'
};
export const SCROLLER = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  px: {
    xs: '16px',
    sm: '24px'
  },
  py: '20px',
  // A flush-to-the-edge scrollbar on a dark panel reads as a seam; inset and dim it.
  '&::-webkit-scrollbar': {
    width: 10
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 8,
    border: '3px solid transparent',
    backgroundClip: 'content-box',
    backgroundColor: 'action.disabled'
  }
};
export const USER_BUBBLE = {
  px: '14px',
  py: '9px',
  maxWidth: '78%',
  borderRadius: '16px 16px 4px 16px',
  bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,.07)' : 'rgba(0,0,0,.05)',
  border: 1,
  borderColor: 'divider'
};

/** Provenance, not decoration: it must never outweigh the answer it describes. */
export const TOOL_CHIP = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 20,
  px: '7px',
  fontSize: 11,
  fontWeight: 400,
  lineHeight: '20px',
  borderRadius: '6px',
  border: 1,
  borderColor: 'divider',
  color: 'text.secondary',
  bgcolor: 'transparent'
};

/**
 * One card shape for everything Livy renders inside an answer: starters, charts and proposals.
 * They were three separate literals that happened to agree, which is a coincidence rather than a
 * decision, and coincidences drift.
 */
export const CARD = {
  borderRadius: '12px',
  border: 1,
  borderColor: 'divider'
};
export const STARTER_CARD = {
  textAlign: 'left',
  justifyContent: 'flex-start',
  textTransform: 'none',
  // MUI centres and clips button labels; a starter is a sentence and has to wrap.
  whiteSpace: 'normal',
  display: 'block',
  px: '14px',
  py: '12px',
  borderRadius: '12px',
  border: 1,
  borderColor: 'divider',
  color: 'text.primary',
  fontSize: 13.5,
  fontWeight: 400,
  lineHeight: 1.45,
  transition: 'border-color .15s, background-color .15s, transform .15s',
  '&:hover': {
    borderColor: 'primary.main',
    bgcolor: 'action.hover',
    transform: 'translateY(-1px)'
  }
};

/**
 * The composer is the only thing on the page a person is meant to act on, so it is drawn as the
 * primary control rather than as another bordered box: more weight at rest, and an unmistakable
 * focus state.
 */
export const COMPOSER_SHELL = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: '8px',
  p: '10px 10px 10px 18px',
  borderRadius: '18px',
  border: 1,
  borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,.16)' : 'rgba(0,0,0,.18)',
  bgcolor: 'background.paper',
  boxShadow: theme => theme.palette.mode === 'dark' ? '0 2px 14px rgba(0,0,0,.45)' : '0 2px 12px rgba(16,24,32,.09)',
  transition: 'border-color .15s, box-shadow .15s',
  '&:hover': {
    borderColor: 'text.disabled'
  },
  '&:focus-within': {
    borderColor: 'primary.main',
    boxShadow: theme => "0 0 0 3px ".concat(theme.palette.primary.main, "2e, 0 2px 14px rgba(0,0,0,.28)")
  }
};

/**
 * Separates one exchange from the next. Without it every turn carried the same weight and a long
 * transcript read as one undifferentiated column.
 */
export const EXCHANGE_START = {
  mt: '34px',
  pt: '30px',
  borderTop: 1,
  borderColor: 'divider'
};

/** The transcript scrolls under the composer rather than stopping dead against it. */
export const COMPOSER_FADE = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  height: 28,
  pointerEvents: 'none',
  background: theme => "linear-gradient(to bottom, transparent, ".concat(theme.palette.background.paper, ")")
};

/** Marks the live edge of a streaming reply, so a pause reads as thinking rather than as finished. */
export const CARET = {
  display: 'inline-block',
  width: '2px',
  height: '1em',
  ml: '2px',
  verticalAlign: '-2px',
  bgcolor: 'primary.main',
  animation: 'livyBlink 1s steps(2, start) infinite',
  '@keyframes livyBlink': {
    '50%': {
      opacity: 0
    }
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none'
  }
};
export const PILL = {
  textTransform: 'none',
  fontSize: 12.5,
  fontWeight: 400,
  lineHeight: '18px',
  minHeight: 26,
  py: '3px',
  px: '11px',
  borderRadius: '13px',
  border: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  color: 'text.primary',
  '&:hover': {
    borderColor: 'primary.main',
    bgcolor: 'action.hover'
  }
};